import React from "react";
import Layer from "../../Layer";
import PageHeader from "../../Component/PageHeader";
import CardInServiceSummaryResults from "../../Component/Card/Report/CardInServiceSummaryResults";
import CardReportSettings from "../../Component/Card/Report/CardReportSettings";
import moment from "moment";
import api from "../../Seating/Security/api";
import User from "../../Utility/Crud/User";
import Security from "../../Utility/Security";

export default class InServiceSummary extends Layer {
  renderHeader() {
    return <PageHeader title={this.getTitle()} />;
  }

  getTitle() {
    return "In-Service Summary";
  }

  getMaxWidth() {
    return 1400;
  }

  /**
   * Groups items by region/location, builds columns,
   * and prepares the rows for rendering.
   *
   * @param {object} settings - Optional report settings containing dateRange.
   * @param {string|null} settings.dateRange.date - Start date filter.
   */
  async getInServiceSummary(settings) {
    this.setState({ loading: true });
    const date = settings?.date?.date || null;
    try {
      const res = await api
        .send("POST", "/reports/inServiceSummaryReport", { date })
        .then((response) => {
          const items = [];
          response.forEach((item) => {
            if (
              item.activity_AccountLocationId === null ||
              item.activity_AccountLocationId === undefined
            ) {
              item.activity_AccountLocationId = 999999; // Assign to 'Unassigned' location
            }
            items.push(item);
          });
          return items;
        });

      const columns = this.buildColumns();
      const defaultColumnWidths = this.buildColumnWidths(columns);
      const newData = this.groupByRegionId(res);
      const { locationRows, regionTotals, itemsGroupedByLocation } =
        this.buildRowsFromGroupedData(newData);
      const grandTotalRow = this.buildGrandTotalRow(locationRows);

      const rows = this.assembleFinalRows(
        locationRows,
        regionTotals,
        grandTotalRow,
      );
      const exportFilename = `${this.getTitle()} - ${moment(this.loadedAt).format("YYYY-MM-DD h mm a")}.csv`;

      this.setState({
        rows: rows,
        itemsGroupedByLocation: itemsGroupedByLocation,
        loading: false,
        defaultColumnWidths: defaultColumnWidths,
        columns: columns,
        loadedAt: new Date(),
        exportFilename: exportFilename,
      });
    } catch (e) {
      console.error("InServiceSummary Report failed", e);
    }
  }

  /**
   * Builds the column definitions for the scorecard table.
   * @returns {Array<object>} Array of column definitions.
   */
  buildColumns() {
    return [
      {
        title: "Region / Location",
        name: "regionLocation",
        type: "string",
        width: 50,
        align: "left",
      },
      {
        title: "Manager",
        name: "manager",
        type: "string",
        width: 20,
        align: "left",
      },
      {
        title: "With Sales Rep",
        name: "countSalesManagerAttended",
        type: "number",
        width: 10,
        align: "left",
      },
      {
        title: "In Open Territory",
        name: "countInOpenTerritory",
        type: "number",
        width: 10,
        align: "left",
      },
      {
        title: "With Target Account",
        name: "countAccountWasTarget",
        type: "number",
        width: 10,
        align: "left",
      },
      {
        title: "Was Top Account",
        name: "countWasTopAccount",
        type: "number",
        width: 10,
        align: "left",
      },
      { title: "", name: "menu", type: "string", width: 60, align: "right" },
    ];
  }

  buildColumnWidths(columns) {
    return columns.map((col) => ({
      columnName: col.name,
      width: col.width || 160,
    }));
  }
  /**
   * Groups a list of data items by their region id.
   * Skips locations not in the current user's regions.
   *
   * @param {Array<object>} dataList - Delivered/approved data items.
   * @returns {object} Object with { regions: { [regionId]: items[] } }.
   */
  groupByRegionId(dataList) {
    const result = {};
    for (const item of dataList) {
      const locationId = item.activity_AccountLocationId;
      const location = this.context?.locationsIndexed?.[locationId];
      const regionId = location?.regionId ?? 999999; // Use 999999 for 'Unassigned' region
      if (!regionId) {
        continue;
      }
      if (!result[regionId]) {
        result[regionId] = [];
      }
      result[regionId].push(item);
    }
    return { regions: result };
  }

  /**
   * Groups a list of items by their activity_AccountLocationId field.
   *
   * @param {Array<object>} list - Data items.
   * @returns {object} Map of { [locationId]: items[] }.
   */
  groupByLocationId(list) {
    return list.reduce((acc, item) => {
      const id = item.activity_AccountLocationId || 999999;
      if (!acc[id]) {
        acc[id] = [];
      }
      acc[id].push(item);
      return acc;
    }, {});
  }

  /**
   * Builds location-level and region-total rows from grouped data.
   * @param {object} data - Grouped data by region/location.
   * @returns {object} { locationRows, regionTotals }.
   */
  buildRowsFromGroupedData(data) {
    const locationRows = [];
    const regionTotals = {};
    let itemsGroupedByLocation = {};
    const userLocations = User.getFilteredLocations(
      this.context.currentUser,
      this.context.userprofileLocationsIndexed,
      this.context.locationsIndexed,
    ).map((location) => location.id);
    userLocations.push(999999); // Grant every user access to 'Unknown Location'
    const userRegions = new Set(
      User.getFilteredLocations(
        this.context.currentUser,
        this.context.userprofileLocationsIndexed,
        this.context.locationsIndexed,
      )
        .map((location) => location.regionId)
        .filter(Boolean),
    );
    userRegions.add(999999); // Grant every user access to 'Unknown Region'
    const viewAllForLocation = Security.hasPermission(
      "inServiceSummary.viewAllForLocation",
    );

    for (const [regionId, items] of Object.entries(data.regions)) {
      const regionName =
        this.context?.regionsIndexed?.[regionId]?.name || "Unknown Region";

      const regionTotal = {
        id: `total-${regionName}`,
        regionId: regionId,
        rowType: "region-total",
        manager: "",
        region: regionName,
        location: "Region Total",
        countAccountWasTarget: 0,
        countWasTopAccount: 0,
        countInOpenTerritory: 0,
        countSalesManagerAttended: 0,
        hide: false,
      };

      const itemsByLocation = this.groupByLocationId(items);
      itemsGroupedByLocation = {
        ...itemsGroupedByLocation,
        ...itemsByLocation,
      };

      for (const [locationId] of Object.entries(itemsByLocation)) {
        const location = this.context.locationsIndexed?.[locationId];

        if (!location?.jsonTierPoints && Number(locationId) !== 999999) {
          continue;
        }

        const row = {
          id: Number(locationId),
          rowType: "location",
          region: regionName,
          manager:
            User.getDisplayName(
              this.context.usersIndexed[location?.salesManagerUserprofileId],
            ) || "",
          managerId: location?.salesManagerUserprofileId || null,
          location: location?.name || `Unknown Location`,
          countAccountWasTarget: 0,
          countWasTopAccount: 0,
          countInOpenTerritory: 0,
          countSalesManagerAttended: 0,
          hide: false,
        };

        itemsByLocation[locationId].forEach((item) => {
          if (
            item?.activity_AppointmentActivity_AccountWasTarget === true &&
            (item?.activity_AppointmentActivity_InOpenTerritory === true ||
              item?.activity_AppointmentActivity_SalesManagerAttended === true)
          ) {
            row.countAccountWasTarget += 1;
          }
          if (
            item?.activity_AppointmentActivity_WasTopAccount === true &&
            (item?.activity_AppointmentActivity_InOpenTerritory === true ||
              item?.activity_AppointmentActivity_SalesManagerAttended === true)
          ) {
            row.countWasTopAccount += 1;
          }
          if (item?.activity_AppointmentActivity_InOpenTerritory === true) {
            row.countInOpenTerritory += 1;
          }
          if (
            item?.activity_AppointmentActivity_SalesManagerAttended === true
          ) {
            row.countSalesManagerAttended += 1;
          }
        });

        regionTotal.countAccountWasTarget += row.countAccountWasTarget;
        regionTotal.countWasTopAccount += row.countWasTopAccount;
        regionTotal.countInOpenTerritory += row.countInOpenTerritory;
        regionTotal.countSalesManagerAttended += row.countSalesManagerAttended;

        // hide location row 999999 if it's unassigned and has no activity
        if (
          row.id === 999999 &&
          row.countAccountWasTarget === 0 &&
          row.countInOpenTerritory === 0 &&
          row.countSalesManagerAttended === 0
        ) {
          row.hide = true;
        }

        if (
          !viewAllForLocation &&
          !userLocations.includes(Number(locationId))
        ) {
          row.hide = true;
        }
        locationRows.push(row);
      }

      if (!viewAllForLocation && !userRegions.has(Number(regionId))) {
        regionTotal.hide = true;
      }

      regionTotals[regionName] = regionTotal;
    }

    if (
      regionTotals["Unknown Region"] &&
      regionTotals["Unknown Region"].countAccountWasTarget === 0 &&
      regionTotals["Unknown Region"].countInOpenTerritory === 0 &&
      regionTotals["Unknown Region"].countSalesManagerAttended === 0
    ) {
      regionTotals["Unknown Region"].hide = true;
    }

    return { locationRows, regionTotals, itemsGroupedByLocation };
  }

  /**
   * Assembles the final ordered rows for the table.
   * Includes region totals, location rows, and special rows.
   *
   * @param {Array<object>} locationRows - Rows for each location.
   * @param {object} regionTotals - Totals by region.
   * @returns {Array<object>} Array of all rows in display order.
   */
  assembleFinalRows(locationRows, regionTotals, grandTotalRow) {
    const rows = [];
    const grouped = locationRows.reduce((acc, row) => {
      acc[row.region] = acc[row.region] || [];
      acc[row.region].push(row);
      return acc;
    }, {});

    if (regionTotals["Unknown Region"]) {
      rows.push(
        regionTotals["Unknown Region"],
        ...(grouped["Unknown Region"] || []).sort((a, b) =>
          a.location.localeCompare(b.location),
        ),
      );
    }

    for (const regionName of Object.keys(regionTotals).sort()) {
      if (regionName === "Unknown Region") continue;
      rows.push(
        regionTotals[regionName],
        ...(grouped[regionName] || []).sort((a, b) =>
          a.location.localeCompare(b.location),
        ),
      );
    }

    rows.push(grandTotalRow);

    return rows;
  }

  /**
   * Builds the overall grand total row across all locations.
   *
   * @param {Array<object>} locationRows - Rows for each location.
   * @returns {object} Grand total row.
   */
  buildGrandTotalRow(locationRows) {
    return {
      id: "grand-total",
      rowType: "grand-total",
      regionId: null,
      region: "Total",
      location: "Total",
      manager: "",
      countAccountWasTarget: locationRows.reduce(
        (sum, row) => sum + (row.countAccountWasTarget ?? 0),
        0,
      ),
      countWasTopAccount: locationRows.reduce(
        (sum, row) => sum + (row.countWasTopAccount ?? 0),
        0,
      ),
      countInOpenTerritory: locationRows.reduce(
        (sum, row) => sum + (row.countInOpenTerritory ?? 0),
        0,
      ),
      countSalesManagerAttended: locationRows.reduce(
        (sum, row) => sum + (row.countSalesManagerAttended ?? 0),
        0,
      ),
      hide: false,
    };
  }

  /**
   * Renders the results card with all scorecard data.
   * @returns {JSX.Element} CardInServiceSummaryResults component.
   */
  renderContent() {
    return (
      <React.Fragment>
        <CardReportSettings
          onRun={(settings) => this.getInServiceSummary(settings)}
          isLoading={this.state.loading}
          settingComponents={{
            date: {
              dateViews: ["month", "year"],
              validationRules: {
                date: {
                  required: true,
                },
              },
            },
          }}
        />
        <CardInServiceSummaryResults
          rows={this.state.rows}
          itemsGroupedByLocation={this.state.itemsGroupedByLocation}
          columns={this.state.columns}
          defaultColumnWidths={this.state.defaultColumnWidths}
          isLoading={this.state.loading}
          loadedAt={this.state.loadedAt}
          title={this.getTitle()}
          exportFilename={this.state.exportFilename}
        />
      </React.Fragment>
    );
  }
}
